import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, Instagram, MapPin, Save, Timer, MessageCircle, Download, Image as ImageIcon, Music2, Coins, Megaphone, Facebook, Crown } from "lucide-react";
import api, { apiErr } from "../lib/api";
import Btn from "../components/Btn";

const CONTACT_FIELDS = [
  { key: "email", label: "Email", icon: Mail, placeholder: "halo@skorup.io" },
  { key: "whatsapp", label: "WhatsApp", icon: Phone, placeholder: "+62 812-3456-7890" },
  { key: "instagram", label: "Instagram", icon: Instagram, placeholder: "@skorup.io" },
  { key: "tiktok", label: "TikTok", icon: Music2, placeholder: "@skorup.io" },
  { key: "facebook", label: "Facebook", icon: Facebook, placeholder: "SkorUp.io" },
  { key: "address", label: "Lokasi / Alamat", icon: MapPin, placeholder: "Jakarta, Indonesia" },
];

// datetime-local expects "YYYY-MM-DDTHH:mm"
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function AdminContact() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/site-settings").then(({ data }) => setForm({
      email: data.email || "", whatsapp: data.whatsapp || "", instagram: data.instagram || "", tiktok: data.tiktok || "", facebook: data.facebook || "", address: data.address || "",
      whatsapp_enabled: data.whatsapp_enabled !== false,
      countdown_enabled: data.countdown_enabled !== false,
      countdown_label: data.countdown_label || "",
      countdown_target: toLocalInput(data.countdown_target),
      banner_enabled: data.banner_enabled !== false,
      banner_title: data.banner_title || "",
      banner_subtitle: data.banner_subtitle || "",
      banner_cta: data.banner_cta || "",
      banner_target: toLocalInput(data.banner_target),
    })).catch(() => {});
  }, []);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (payload.countdown_target) payload.countdown_target = new Date(payload.countdown_target).toISOString();
      if (payload.banner_target) payload.banner_target = new Date(payload.banner_target).toISOString();
      await api.put("/admin/site-settings", payload);
      toast.success("Pengaturan berhasil diperbarui!");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (!form) return <div className="min-h-[30vh] grid place-items-center"><div className="w-10 h-10 rounded-full border-4 border-su-border border-t-su-primary animate-spin" /></div>;

  const inputCls = "w-full bg-su-bg border-2 border-su-border rounded-xl px-4 py-2.5 font-medium focus:border-su-primary outline-none transition-colors";
  const lbl = "text-xs font-heading font-black uppercase text-su-muted mb-1.5 block";

  return (
    <div className="space-y-6 max-w-2xl">
      {/* contact */}
      <div className="card-chunky p-6" data-testid="admin-contact">
        <h2 className="font-heading font-black text-lg mb-1">Info Kontak Landing Page</h2>
        <p className="text-su-muted font-medium text-sm mb-5">Tampil di bagian "Hubungi Kami" pada halaman utama.</p>
        <div className="space-y-4">
          {CONTACT_FIELDS.map((f) => (
            <div key={f.key}>
              <label className="text-xs font-heading font-black uppercase text-su-muted flex items-center gap-1.5 mb-1.5"><f.icon className="w-3.5 h-3.5" /> {f.label}</label>
              <input data-testid={`contact-input-${f.key}`} value={form[f.key]} onChange={(e) => set(f.key, e.target.value)} placeholder={f.placeholder}
                className="w-full bg-su-bg border-2 border-su-border rounded-xl px-4 py-2.5 font-medium focus:border-su-primary outline-none transition-colors" />
            </div>
          ))}
        </div>
        <label className="flex items-center gap-3 mt-4 cursor-pointer">
          <input type="checkbox" data-testid="toggle-whatsapp" checked={form.whatsapp_enabled} onChange={(e) => set("whatsapp_enabled", e.target.checked)} className="w-5 h-5 accent-[var(--su-primary)]" />
          <span className="font-heading font-bold text-sm flex items-center gap-1.5"><MessageCircle className="w-4 h-4 text-[#25D366]" /> Tampilkan tombol WhatsApp melayang</span>
        </label>
      </div>

      {/* countdown */}
      <div className="card-chunky p-6" data-testid="admin-countdown">
        <h2 className="font-heading font-black text-lg mb-1 flex items-center gap-2"><Timer className="w-5 h-5 text-su-primary" /> Countdown Landing Page</h2>
        <p className="text-su-muted font-medium text-sm mb-5">Atur hitung mundur menuju ujian di halaman utama.</p>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-heading font-black uppercase text-su-muted mb-1.5 block">Label</label>
            <input data-testid="countdown-label" value={form.countdown_label} onChange={(e) => set("countdown_label", e.target.value)} placeholder="Menuju SKD CPNS 2026"
              className="w-full bg-su-bg border-2 border-su-border rounded-xl px-4 py-2.5 font-medium focus:border-su-primary outline-none transition-colors" />
          </div>
          <div>
            <label className="text-xs font-heading font-black uppercase text-su-muted mb-1.5 block">Tanggal & Waktu Target</label>
            <input type="datetime-local" data-testid="countdown-target" value={form.countdown_target} onChange={(e) => set("countdown_target", e.target.value)}
              className="w-full bg-su-bg border-2 border-su-border rounded-xl px-4 py-2.5 font-medium focus:border-su-primary outline-none transition-colors" />
          </div>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" data-testid="toggle-countdown" checked={form.countdown_enabled} onChange={(e) => set("countdown_enabled", e.target.checked)} className="w-5 h-5 accent-[var(--su-primary)]" />
            <span className="font-heading font-bold text-sm">Tampilkan countdown di landing page</span>
          </label>
        </div>
      </div>

      {/* akbar banner */}
      <div className="card-chunky p-6" data-testid="admin-banner">
        <h2 className="font-heading font-black text-lg mb-1 flex items-center gap-2"><Megaphone className="w-5 h-5 text-su-danger" /> Banner Tryout Akbar (Landing)</h2>
        <p className="text-su-muted font-medium text-sm mb-5">Banner promosi di atas landing page. Diklik akan mengarah ke halaman login/daftar.</p>
        <div className="space-y-4">
          <div>
            <label className={lbl}>Judul Banner</label>
            <input data-testid="banner-title" value={form.banner_title} onChange={(e) => set("banner_title", e.target.value)} placeholder="🔥 Tryout Akbar Nasional #1" className={inputCls} />
          </div>
          <div>
            <label className={lbl}>Subjudul</label>
            <input data-testid="banner-subtitle" value={form.banner_subtitle} onChange={(e) => set("banner_subtitle", e.target.value)} placeholder="Simulasi serentak se-Indonesia. Daftar sekarang!" className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={lbl}>Teks Tombol (CTA)</label>
              <input data-testid="banner-cta" value={form.banner_cta} onChange={(e) => set("banner_cta", e.target.value)} placeholder="Ikut Sekarang" className={inputCls} />
            </div>
            <div>
              <label className={lbl}>Countdown Target</label>
              <input type="datetime-local" data-testid="banner-target" value={form.banner_target} onChange={(e) => set("banner_target", e.target.value)} className={inputCls} />
            </div>
          </div>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" data-testid="toggle-banner" checked={form.banner_enabled} onChange={(e) => set("banner_enabled", e.target.checked)} className="w-5 h-5 accent-[var(--su-primary)]" />
            <span className="font-heading font-bold text-sm">Tampilkan banner di landing page</span>
          </label>
        </div>
      </div>

      <Btn data-testid="contact-save" onClick={save} disabled={busy} className="w-full"><Save className="w-4 h-4" /> {busy ? "Menyimpan..." : "Simpan Semua Pengaturan"}</Btn>

      {/* brand / logo */}
      <div className="card-chunky p-6" data-testid="admin-brand">
        <h2 className="font-heading font-black text-lg mb-1 flex items-center gap-2"><ImageIcon className="w-5 h-5 text-su-primary" /> Logo & Brand SkorUp.io</h2>
        <p className="text-su-muted font-medium text-sm mb-5">Unduh aset logo resmi untuk keperluan promosi & media sosial.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="rounded-2xl border-2 border-su-border p-4 text-center">
            <div className="h-28 grid place-items-center mb-3"><img src="/brand/skorup_icon.png" alt="Ikon SkorUp" className="h-24 w-24 rounded-2xl object-cover" /></div>
            <p className="font-heading font-bold text-sm mb-2">Ikon (Squircle)</p>
            <div className="flex gap-2 justify-center">
              <a href="/brand/skorup_icon.png" download="skorup-icon.png" data-testid="download-icon-png" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border-2 border-su-border font-heading font-extrabold text-xs hover:border-su-primary transition-colors"><Download className="w-3.5 h-3.5" /> PNG</a>
            </div>
          </div>
          <div className="rounded-2xl border-2 border-su-border p-4 text-center" style={{ background: "#0b0b13" }}>
            <div className="h-28 grid place-items-center mb-3"><img src="/brand/skorup_wordmark.png" alt="Logo SkorUp.io" className="max-h-24 object-contain" /></div>
            <p className="font-heading font-bold text-sm mb-2 text-white">Wordmark (Transparan)</p>
            <div className="flex gap-2 justify-center">
              <a href="/brand/skorup_wordmark.png" download="skorup-wordmark.png" data-testid="download-wordmark-png" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border-2 border-white/30 text-white font-heading font-extrabold text-xs hover:border-su-primary transition-colors"><Download className="w-3.5 h-3.5" /> PNG</a>
            </div>
          </div>
        </div>
      </div>

      <CostsCard />
    </div>
  );
}

const COST_ORDER = ["drill", "arena", "mega_paid", "module"];

const COST_PRESETS = {
  hemat: { tryout: { token: 1, energy: 0 }, drill: { token: 0, energy: 5 }, arena: { token: 0, energy: 0 }, mega_paid: { token: 10, energy: 30 }, module: { token: 0, energy: 0 } },
  normal: { tryout: { token: 2, energy: 0 }, drill: { token: 1, energy: 8 }, arena: { token: 1, energy: 0 }, mega_paid: { token: 20, energy: 50 }, module: { token: 1, energy: 0 } },
  mahal: { tryout: { token: 4, energy: 0 }, drill: { token: 2, energy: 12 }, arena: { token: 2, energy: 0 }, mega_paid: { token: 30, energy: 80 }, module: { token: 2, energy: 0 } },
};
const PRESET_META = [
  { id: "hemat", label: "Hemat", emoji: "💚" },
  { id: "normal", label: "Normal", emoji: "⚖️" },
  { id: "mahal", label: "Mahal", emoji: "🔥" },
];

function CostsCard() {
  const [costs, setCosts] = useState(null);
  const [labels, setLabels] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/costs").then(({ data }) => { setCosts(data.costs); setLabels(data.labels); }).catch(() => {});
  }, []);

  const setVal = (act, field, v) => setCosts((c) => ({ ...c, [act]: { ...c[act], [field]: Math.max(0, Number(v) || 0) } }));

  const applyPreset = (id) => {
    const p = COST_PRESETS[id];
    setCosts((c) => {
      const next = { ...c };
      COST_ORDER.forEach((a) => { next[a] = { ...next[a], ...p[a] }; });
      return next;
    });
    toast.success(`Preset "${id}" diterapkan. Klik Simpan untuk mengaktifkan.`);
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {};
      COST_ORDER.forEach((a) => { if (costs[a]) payload[a] = { token: Number(costs[a].token) || 0, energy: Number(costs[a].energy) || 0 }; });
      await api.put("/admin/costs", payload);
      toast.success("Biaya fitur diperbarui!");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (!costs) return null;
  const cell = "w-20 bg-su-bg border-2 border-su-border rounded-xl px-3 py-2 font-heading font-bold text-center focus:border-su-primary outline-none transition-colors";

  return (
    <div className="card-chunky p-6" data-testid="admin-costs">
      <h2 className="font-heading font-black text-lg mb-1 flex items-center gap-2"><Coins className="w-5 h-5 text-su-secondaryShadow" /> Biaya Fitur (Token & Energi)</h2>
      <p className="text-su-muted font-medium text-sm mb-4">Atur berapa token & energi yang dipotong saat siswa membuka tiap fitur. Set 0 untuk gratis.</p>
      <div className="flex flex-wrap gap-2 mb-5">
        <span className="text-xs font-heading font-black uppercase text-su-muted self-center mr-1">Preset cepat:</span>
        {PRESET_META.map((p) => (
          <button key={p.id} data-testid={`preset-${p.id}`} onClick={() => applyPreset(p.id)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border-2 border-su-border font-heading font-extrabold text-sm hover:border-su-primary hover:text-su-primary transition-colors">
            <span>{p.emoji}</span> {p.label}
          </button>
        ))}
      </div>
      <div className="space-y-3">
        <div className="grid grid-cols-[1fr_auto_auto] gap-3 items-center px-1">
          <span className="text-xs font-heading font-black uppercase text-su-muted">Fitur</span>
          <span className="text-xs font-heading font-black uppercase text-su-secondaryShadow w-20 text-center">Token</span>
          <span className="text-xs font-heading font-black uppercase text-su-energy w-20 text-center">Energi</span>
        </div>
        {COST_ORDER.map((a) => costs[a] && (
          <div key={a} className="grid grid-cols-[1fr_auto_auto] gap-3 items-center" data-testid={`cost-row-${a}`}>
            <span className="font-heading font-bold text-sm">{labels[a] || a}{a === "tryout" && <span className="block text-[11px] text-su-muted font-medium">energi diatur per paket di tab Tryout</span>}</span>
            <input type="number" data-testid={`cost-${a}-token`} value={costs[a].token} onChange={(e) => setVal(a, "token", e.target.value)} className={cell} />
            <input type="number" data-testid={`cost-${a}-energy`} value={costs[a].energy} onChange={(e) => setVal(a, "energy", e.target.value)} disabled={a === "tryout"} className={cell + (a === "tryout" ? " opacity-40" : "")} />
          </div>
        ))}
      </div>
      <Btn data-testid="costs-save" onClick={save} disabled={busy} className="w-full mt-5"><Save className="w-4 h-4" /> {busy ? "Menyimpan..." : "Simpan Biaya Fitur"}</Btn>

      <ArenaRewardCard />
    </div>
  );
}

function ArenaRewardCard() {
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/arena-reward").then(({ data }) => setR(data.reward)).catch(() => {}); }, []);
  if (!r) return null;
  const set = (k, v) => setR((s) => ({ ...s, [k]: Number(v) }));
  const save = async () => {
    setBusy(true);
    try { await api.put("/admin/arena-reward", r); toast.success("Hadiah juara disimpan"); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const cell = "w-full px-3 py-2 rounded-xl border-2 border-su-border bg-su-bg text-su-text font-heading font-black text-center outline-none focus:border-su-primary";
  return (
    <div className="card-chunky p-6 mt-6" data-testid="arena-reward-config">
      <div className="flex items-center gap-2 mb-1"><Crown className="w-5 h-5 text-su-secondary" /><h3 className="font-heading font-black text-lg">Hadiah Juara Arena Mingguan</h3></div>
      <p className="text-su-muted font-medium text-sm mb-4">Diberikan otomatis ke juara #1 papan <b>CPNS</b> & <b>SNBT</b> tiap pekan.</p>
      <div className="grid grid-cols-3 gap-3">
        <label className="block"><span className="text-xs font-heading font-black uppercase text-su-secondaryShadow">Token</span>
          <input type="number" data-testid="reward-tokens" value={r.tokens} onChange={(e) => set("tokens", e.target.value)} className={cell + " mt-1"} /></label>
        <label className="block"><span className="text-xs font-heading font-black uppercase text-su-energy">Energi</span>
          <input type="number" data-testid="reward-energy" value={r.energy} onChange={(e) => set("energy", e.target.value)} className={cell + " mt-1"} /></label>
        <label className="block"><span className="text-xs font-heading font-black uppercase text-su-primary">VIP (hari)</span>
          <input type="number" data-testid="reward-vip" value={r.vip_days} onChange={(e) => set("vip_days", e.target.value)} className={cell + " mt-1"} /></label>
      </div>
      <Btn data-testid="reward-save" onClick={save} disabled={busy} className="w-full mt-5"><Save className="w-4 h-4" /> {busy ? "Menyimpan..." : "Simpan Hadiah Juara"}</Btn>
    </div>
  );
}
